import React from "react";
//import Modal from "react-responsive-modal";
import ReactModal from "react-modal";
import { branch } from "baobab-react/higher-order";
import autoBind from "react-autobind";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import * as actions from "../actions/actions";
import { getJwtToken } from "../actions/user";
import env from "../constants/env";
import PlayerPaths from "../constants/paths/playerPaths";
import ambifiWebPlayerConfig from "../constants/webPlayerConfig/ambifi";
import PlayerZoid from "../components/player/playerZoid";
import AppConstants from "../constants/appConstants";
import { buildPreviewDrivePayloadForChecklist } from "../utils/previewDriveUtils";
import { buildPreviewWebPlayerState, buildAuthoringPreviewActivity, buildAuthoringPreviewConfig, buildAuthoringPreviewLaunchContext, createAuthoringPreviewSessionId } from "../utils/previewPlayerStateUtils";

import PreviewWrapper from "./shared/previewWrapper";

function hashPreviewString(value = "") {
	let hash = 0;
	const text = _.toString(value);
	for (let i = 0; i < text.length; i += 1) {
		hash = ((hash << 5) - hash) + text.charCodeAt(i);
		hash |= 0;
	}
	return `${text.length}:${hash}`;
}

function getChecklistContentSignature(checklist = {}) {
	if (!_.isObject(checklist)) {
		return "";
	}

	try {
		return hashPreviewString(JSON.stringify(checklist));
	} catch (err) {
		return hashPreviewString(`${_.get(checklist, "revision", "")}:${_.get(checklist, "revisionTs", "")}:${_.get(checklist, "name", "")}`);
	}
}

class ChecklistPreviewModal extends React.PureComponent {

	constructor(props) {
		super(props);
		autoBind(this);
		this.webPlayerRef = React.createRef();
		this.previewLoadSignature = this.getPreviewLoadSignature(props);
		this.state = {
			fallbackToTop: false
		};
		this.initWebPlayerState(props);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		this.resetPreviewLoadFallback(nextProps);
		if (
			this.getPreviewLoadSignature(this.props) !== this.getPreviewLoadSignature(nextProps) ||
			this.props.user !== nextProps.user ||
			this.props.selectedOrgId !== nextProps.selectedOrgId ||
			this.props.selectedOrgMember !== nextProps.selectedOrgMember
		) {
			this.initWebPlayerState(nextProps);
		}
	}

	getPreviewLoadSignature(props = this.props) {
		const checklistId = _.get(props, "previewChecklist.checklist.id", "");
		const session = _.get(props, "previewChecklist.session", "");
		const showModal = _.get(props, "previewChecklist.showModal", false);
		const activityVarsProfileKey = _.get(props, "previewChecklist.activityVarsProfileKey", "");
		const activityVars = JSON.stringify(_.get(props, "previewChecklist.activityVars", {}));
		const checklistContent = getChecklistContentSignature(_.get(props, "previewChecklist.checklist", {}));
		return `${checklistId}:${session}:${showModal}:${activityVarsProfileKey}:${activityVars}:${checklistContent}`;
	}

	resetPreviewLoadFallback(nextProps = this.props) {
		const nextSignature = this.getPreviewLoadSignature(nextProps);
		if (nextSignature !== this.previewLoadSignature) {
			this.previewLoadSignature = nextSignature;
			if (this.state.fallbackToTop) {
				this.setState({ fallbackToTop: false });
			}
		}
	}

	getEffectiveChecklist() {
		return buildAuthoringPreviewActivity(_.get(this.props, "previewChecklist.checklist", null), {
			fallbackToTop: this.state.fallbackToTop === true
		});
	}

	handleOnLoadError(err) {
		console.error("Unable to load modal preview player", err);
		actions.hideDocumentsSpinner();

		if (this.state.fallbackToTop !== true) {
			this.initWebPlayerState(this.props);
			this.setState({ fallbackToTop: true });
		}
	}

	handleExit() {
		this.props.dispatch(actions.closePreviewModal);
	}

	initWebPlayerState(props) {
		this.previewSessionId = createAuthoringPreviewSessionId();
		const selectedOrg = props.selectedOrgId || props.selectedOrgMember ? {
			id: props.selectedOrgId,
			member: props.selectedOrgMember || null
		} : null;

		this.newWebPlayerState = buildPreviewWebPlayerState({
			user: props.user,
			selectedOrg,
			config: { showControls: false, showHeaderPanel: true, startInShowMode: true }
		});
	}

	render() {
		if (!this.props.previewChecklist || (this.props.previewChecklist && !this.props.previewChecklist.checklist.hasOwnProperty("id"))) {
			return null;
		}

			let closeButton =
				<div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", position: "absolute", right: "10px", top: "12px", width: "32px" }}>
					<div className="custom-player-modal-close-button">
						<FontAwesomeIcon icon="times" onClick={() => this.props.dispatch(actions.closePreviewModal)} />
					</div>
				</div>;
		// let modalTitle = "Preview";
		const effectiveChecklist = this.getEffectiveChecklist();
		let orgPreferences = {};
		if (this.props.selectedOrgPreferences) {
			orgPreferences = _.pick(this.props.selectedOrgPreferences, ["tts-phonetics", "theme", "stylesheet", "fonts", "playlists"]); // Limit this for now to prevent very large props
		}
		const addtlProps = {
			key: `cache-key-${this.previewSessionId}-${this.getPreviewLoadSignature(this.props)}-${this.state.fallbackToTop ? "top" : "selected"}`
		};
		let credentials = "";
		if (window.awsTempCredentials) {
			credentials = btoa(JSON.stringify({
				iid: _.get(this.props, "user.identityId", ""),
				sak: window.awsTempCredentials.secretAccessKey,
				ak: window.awsTempCredentials.accessKeyId,
				st: window.awsTempCredentials.sessionToken
			}));
		}
		const previewPlayerConfig = buildAuthoringPreviewConfig(ambifiWebPlayerConfig, {
			activityId: _.get(this.props, "previewChecklist.checklist.id", "preview"),
			sessionId: this.previewSessionId
		});
		const previewLaunchContext = buildAuthoringPreviewLaunchContext(this.props.previewChecklist);

		const previewDrivePayload = buildPreviewDrivePayloadForChecklist(effectiveChecklist, this.props.drive || {});
		let activity = effectiveChecklist ? <div style={{ flex: 1 }} className="ambifi-web-player-container status"><PlayerZoid
			activities={[effectiveChecklist]}
			activityId={effectiveChecklist.id}
			activityVars={previewLaunchContext.activityVars}
			launchContext={previewLaunchContext}
			config={previewPlayerConfig}
			env={_.cloneDeep(env)}
			drive={previewDrivePayload}
			state={this.newWebPlayerState}
			credentials={credentials}
			jwt={getJwtToken()}
			componentRequester={"ambifiCloud"}
			onExit={this.handleExit}
			onLoad={() => actions.hideDocumentsSpinner()}
			onLoadError={this.handleOnLoadError}
			debugVarsInspector={true}
			orgPreferences={orgPreferences}
			ref={this.webPlayerRef}
			{...addtlProps} /></div> : <div><FontAwesomeIcon
				name='spinner'
				spin /> Loading activity for preview, please wait...</div>;




		const component = <ReactModal style={{ overlay: { zIndex: AppConstants.react.components.reactModal.zIndex, backgroundColor: "#666666" } }} className="custom-player-modal" isOpen={this.props.previewChecklist.showModal} little>
			{/* <h3 style={{ textAlign: "center" }}>{modalTitle}</h3> */}
			<div style={{ display: "flex", height: "100vh", overflow: "auto", flexDirection: "column" }}>
				<PreviewWrapper compact={true}>
					{activity}
				</PreviewWrapper>
			</div>
			{closeButton}
		</ReactModal>;

		return component;
	}
}

export default branch({
	user: ["user"],
	drive: ["appState", "previewChecklist", "drive"],
	player: PlayerPaths.ROOT,
	selectedActivityId: PlayerPaths.SELECTED_ACTIVITY_ID,
	appCapabilities: ["appCapabilities"],
	selectedOrgId: ["selectedOrg", "id"],
	selectedOrgMember: ["selectedOrg", "member"],
	selectedOrgPreferences: ["selectedOrg", "preferences"],
	previewChecklist: ["appState", "previewChecklist"]
}, ChecklistPreviewModal);
